'use client';

import { useEffect, useState } from 'react';
import type { ComponentProps } from 'react';
import type { CasinoMds } from '@fanduel/casino-mds';
import type { PromotionDetailsPage } from '@fanduel/rewards-hub-client';
import { getMdsEndpoint } from '@/utils/mds/get-mds-endpoint';

type MdsInstanceType = typeof CasinoMds;

// rewards-hub-client's MDSInstance prop is still typed against an older casino-mds
// pre-release (a callable factory whose instance.initSocket() returned a WebSocket); the
// current stable casino-mds returns void from initSocket() instead, and rewards-hub-client
// never reads that return value. Safe type-only bridge until it upgrades its dependency -
// centralized here so every call site doesn't need to repeat the cast.
type RewardsHubMdsInstance = ComponentProps<typeof PromotionDetailsPage>['MDSInstance'];

let sharedInstance: MdsInstanceType | undefined;
let sharedUserId: string | undefined;

const ensureSharedInstance = async (userId: string): Promise<MdsInstanceType | undefined> => {
    if (sharedInstance && sharedUserId === userId) {
        return sharedInstance;
    }

    const endpoint = getMdsEndpoint();
    if (!endpoint) {
        return undefined;
    }

    // Dynamic import: @fanduel/casino-mds touches `window` at module-evaluation time, so it
    // must never be loaded during SSR ('use client' only controls hydration, not whether this
    // module also runs on the server for the initial render).
    const { CasinoMds } = await import('@fanduel/casino-mds');

    // User changed (e.g. re-login): tear down the previous instance first.
    if (sharedInstance && sharedUserId !== userId) {
        sharedInstance.closeConnection();
        sharedInstance = undefined;
        sharedUserId = undefined;
    }

    CasinoMds.initialize(endpoint);
    CasinoMds.initSocket(userId);
    sharedInstance = CasinoMds;
    sharedUserId = userId;
    return sharedInstance;
};

export const resetMdsInstance = () => {
    if (sharedInstance) {
        sharedInstance.closeConnection();
    }
    sharedInstance = undefined;
    sharedUserId = undefined;
};

export const useMdsInstance = (userId: string | undefined) => {
    const [instance, setInstance] = useState<MdsInstanceType | undefined>(undefined);

    useEffect(() => {
        if (!userId) {
            setInstance(undefined);
            return;
        }

        let cancelled = false;
        ensureSharedInstance(userId).then((resolved) => {
            if (!cancelled) {
                setInstance(resolved);
            }
        });
        // No teardown on unmount: the shared instance must outlive mounts to keep realtime events on navigation.
        return () => {
            cancelled = true;
        };
    }, [userId]);

    return instance;
};

export const useMdsInstanceForRewardsHub = (userId: string | undefined): RewardsHubMdsInstance =>
    useMdsInstance(userId) as unknown as RewardsHubMdsInstance;
// TODO: Remove after rewards-hub-client adopts casino-mds 1.0.3
